import {
    Component,
    DestroyRef,
    effect,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    SecurityContext,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    Address,
    ClientDetailModel,
    ClientProfileStatus,
    ClientProfileType,
    ClientsFormModel,
    CountryModel,
    Organization,
    Person,
    UpsertClientProfileDetailRequest,
} from 'lemunzo-api';
import { RegistrationFormType } from '../../enums/registration-form-type';
import { maxDateValidator } from '../../formValidators/max-date-validator';
import { minDateValidator } from '../../formValidators/min-date-validator';
import { CustomFields } from '../../models/client-custom-fields-model';
import {
    AddedElementVisibility,
    ElementType,
} from '../../models/clientsform/added-element-model';
import { BodyCategoryModel } from '../../models/clientsform/body-category-model';
import { BodyElementModel } from '../../models/clientsform/body-element-model';
import { ElementWithOptionsModel } from '../../models/clientsform/element-with-options';
import {
    InputElementModel,
    InputHTMLTypes,
} from '../../models/clientsform/input-element-model';
import { TextareaElementModel } from '../../models/clientsform/textarea-element.model';
import { CountryCode } from '../../models/country-code';
import { LanguageCode } from '../../models/language-code';
import { TranslateDatePipe } from '../../pipes/translate-date.pipe';
import { ConstantsService } from '../../services/constants.service';
import { LocalizationService } from '../../services/localization.service';
import { getEmptyClientProfileModel } from '../../utils/get-empty-client-profile';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { PhoneInputComponent } from '../phone-input/phone-input.component';
import { CheckboxElementComponent } from '../registration/checkbox-element/checkbox-element.component';
import { CountrySelectElementComponent } from '../registration/country-select-element/country-select-element.component';
import { InputElementComponent } from '../registration/input-element/input-element.component';
import { LanguageSelectElementComponent } from '../registration/language-select-element/language-select-element.component';
import { RadioElementComponent } from '../registration/radio-element/radio-element.component';
import { SelectElementComponent } from '../registration/select-element/select-element.component';
import { TextareaElementComponent } from '../registration/textarea-element/textarea-element.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';
import { TranslationComponent } from '../translation/translation.component';

@Component({
    selector: 'apcam-registration-form',
    providers: [TranslateDatePipe],
    imports: [
        CheckboxElementComponent,
        CountrySelectElementComponent,
        InputElementComponent,
        LanguageSelectElementComponent,
        LoadingSpinnerComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatSelectModule,
        PhoneInputComponent,
        RadioElementComponent,
        ReactiveFormsModule,
        SelectElementComponent,
        TextareaElementComponent,
        TooltipCustomComponent,
        TranslatePipe,
        TranslationComponent,
    ],
    templateUrl: './registration-form.component.html',
    styleUrl: './registration-form.component.scss',
})
export class RegistrationFormComponent implements OnInit {
    @Input()
    allowEdit: Signal<boolean> = signal(true);

    @Input()
    bodyData: CustomFields = {};

    @Input({ required: true })
    clientProfileType!: WritableSignal<ClientProfileType | undefined>;

    @Input()
    clientProfile: ClientDetailModel = getEmptyClientProfileModel();

    @Input({ required: true })
    countryList!: Map<string, CountryModel[]>;

    @Input()
    isClientPortal: boolean = false;

    @Input()
    confirmButtonTranslationKey: string | null = null;

    @Input()
    readonly: WritableSignal<boolean> = signal(true);

    @Input({ required: true })
    registrationForm: WritableSignal<ClientsFormModel | null> = signal(null);

    @Input({ required: true })
    registrationFormGroup: WritableSignal<FormGroup | null> = signal(null);

    @Input({ required: true })
    registrationFormType!: RegistrationFormType;

    @Input()
    skipConsentCheckbox: boolean = false;

    @Input()
    submitDisabled: WritableSignal<boolean> = signal(false);

    @Input({ required: true })
    submitting: Signal<boolean> = signal(false);

    @Input()
    supportedCountries: WritableSignal<CountryCode[]> = signal([]);

    @Input({ required: true })
    supportedLanguages: WritableSignal<LanguageCode[]> = signal([]);

    @Output()
    onSubmit: EventEmitter<UpsertClientProfileDetailRequest> =
        new EventEmitter();

    protected get defaultLang() {
        return this.localizationService.defaultLanguage;
    }
    protected get showPlaceholders(): boolean {
        return this.registrationFormType === RegistrationFormType.REGISTER;
    }

    protected readonly checkboxDirty: WritableSignal<boolean> = signal(false);

    protected readonly ClientProfileStatus = ClientProfileStatus;
    protected readonly ElementType = ElementType;
    protected readonly InputHTMLTypes = InputHTMLTypes;
    protected readonly RegistrationFormType = RegistrationFormType;
    protected readonly signal = signal;

    protected checkboxLabelHTML: WritableSignal<SafeUrl> = signal('');

    // lists all fields which have a specific meaning in the clients form - they map to DB fields, not non-SQL data
    private readonly PREDEFINED_DEFAULT_FIELDS = [
        'contactInfo1',
        'contactInfo2',
        'email',
        'phone',
        'userLanguage',
        'dateOfBirth',
        'realWorldId',
        'street',
        'houseNumber',
        'zipCode',
        'city',
        'countryCode',
    ];

    private readonly PROFILE_DATE_STORAGE_FORMAT = 'yyyy-MM-dd';

    private readonly datePipe: TranslateDatePipe = inject(TranslateDatePipe);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly domSanitizer: DomSanitizer = inject(DomSanitizer);
    private readonly formBuilder: FormBuilder = inject(FormBuilder);
    private readonly localizationService: LocalizationService =
        inject(LocalizationService);
    private readonly translateService: TranslateService =
        inject(TranslateService);

    constructor() {
        effect(() => {
            if (this.readonly()) {
                this.registrationFormGroup()?.disable();
            } else {
                this.registrationFormGroup()?.enable();
            }
            this.disableRegistrationOnlyFields();
        });
    }

    ngOnInit(): void {
        if (this.registrationForm() !== null) {
            this.sanitizeCheckboxLabel(
                this.localizationService.getCurrentLanguage() as LanguageCode
            );
            this.translateService.onLangChange
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ({ lang }) =>
                        this.sanitizeCheckboxLabel(lang as LanguageCode),
                });

            this.registrationFormGroup.set(
                this.setRegistrationFormGroup(this.registrationForm()!)
            );
            this.prefillValues();
            this.disableRegistrationOnlyFields();
        }

        // register forms are never readonly
        if (this.registrationFormType === RegistrationFormType.REGISTER) {
            this.readonly.set(false);
        }
    }

    private sanitizeCheckboxLabel(language: LanguageCode): void {
        // Step 1: Sanitize (remove unsafe tags/attributes)
        const cleaned =
            this.domSanitizer.sanitize(
                SecurityContext.HTML,
                this.registrationForm()?.bottom.checkboxText.translations[
                    language
                ] as string
            ) ?? '';

        // // Step 2: Wrap the cleaned string as SafeHtml
        this.checkboxLabelHTML.set(
            this.domSanitizer.bypassSecurityTrustHtml(cleaned)
        );
    }

    /**
     * Tracks the form submit button. Upon confirmation, validates the form and if there are no errors, creates
     * a profile model from the form data and emits an event with the model.
     */
    protected handleSubmit(): void {
        this.registrationFormGroup()?.markAllAsTouched();
        if (this.registrationFormGroup()?.invalid) {
            return;
        }
        this.onSubmit.emit(this.processFormData());
    }

    protected typecastToInputModel(bodyElement: BodyElementModel) {
        return bodyElement as InputElementModel;
    }

    protected typecastToTextareaModel(bodyElement: BodyElementModel) {
        return bodyElement as TextareaElementModel;
    }

    protected typecastToElementWithOptionsModel(bodyElement: BodyElementModel) {
        return bodyElement as ElementWithOptionsModel;
    }

    protected renderElement(bodyElement: BodyElementModel): boolean {
        return !(
            (bodyElement.visible === AddedElementVisibility.AdminOnly &&
                this.isClientPortal) ||
            (bodyElement.visible === AddedElementVisibility.EditOnly &&
                this.registrationFormType === RegistrationFormType.REGISTER)
        );
    }

    protected renderCategory(category: BodyCategoryModel): boolean {
        return category.elements.some((element) => this.renderElement(element));
    }

    protected toggleReadonly(): void {
        if (!this.readonly()) {
            this.prefillValues();
            this.registrationFormGroup()?.markAsPristine();
        }
        this.readonly.update((readonly) => !readonly);
    }

    protected shouldShowRegistrationOnlyTooltip(
        category: BodyCategoryModel,
        bodyElement: BodyElementModel
    ): boolean {
        if (this.readonly()) {
            return false;
        }

        const elementControl = this.resolveBodyElementControl(
            category,
            bodyElement
        );

        return (
            bodyElement.visible === AddedElementVisibility.RegistrationOnly &&
            this.registrationFormType === RegistrationFormType.EDIT &&
            this.isClientPortal &&
            !!elementControl?.disabled
        );
    }

    private setRegistrationFormGroup(formConfig: ClientsFormModel) {
        return this.formBuilder.group({
            body: this.setBodyCategoriesFormGroup(formConfig.body),
            bottom:
                this.registrationFormType === RegistrationFormType.REGISTER
                    ? this.setBottomElementsFormGroup()
                    : new FormGroup({}),
        });
    }

    private setBodyCategoriesFormGroup(
        bodyCategories: BodyCategoryModel[]
    ): FormGroup {
        const bodyGroup = this.formBuilder.group({});

        for (const category of bodyCategories) {
            if (!this.renderCategory(category)) {
                continue;
            }
            const categoryGroup = this.formBuilder.group({});
            for (const element of category.elements) {
                if (!this.renderElement(element)) {
                    continue;
                }
                const validators = this.setBodyElementValidators(element);
                categoryGroup.addControl(
                    element.name.translations[this.defaultLang],
                    new FormControl('', validators)
                );
            }
            bodyGroup.addControl(category.name, categoryGroup);
        }

        return bodyGroup;
    }

    private disableRegistrationOnlyFields(): void {
        // In admin portal and within client registration, all fields are editable
        if (
            this.registrationFormType !== RegistrationFormType.EDIT ||
            !this.isClientPortal
        ) {
            return;
        }

        const bodyFormGroup = this.registrationFormGroup()?.get('body') as
            FormGroup | undefined;
        if (!bodyFormGroup || !this.registrationForm()) {
            return;
        }

        for (const category of this.registrationForm()!.body) {
            const categoryGroup = bodyFormGroup.get([category.name]) as
                FormGroup | undefined;
            if (!categoryGroup) {
                continue;
            }

            for (const element of category.elements) {
                if (
                    element.visible !== AddedElementVisibility.RegistrationOnly
                ) {
                    continue;
                }

                const controlName = element.name.translations[this.defaultLang];
                categoryGroup.get([controlName])?.disable({ emitEvent: false });
            }
        }
    }

    private resolveBodyElementControl(
        category: BodyCategoryModel,
        bodyElement: BodyElementModel
    ): FormControl | null {
        const controlName = bodyElement.name.translations[this.defaultLang];
        if (!controlName) {
            return null;
        }

        const bodyFormGroup = this.registrationFormGroup()?.get('body') as
            FormGroup | undefined;
        const categoryGroup = bodyFormGroup?.get([category.name]) as
            FormGroup | undefined;
        const control = categoryGroup?.get([controlName]);

        return control instanceof FormControl ? control : null;
    }

    private setBodyElementValidators(config: BodyElementModel): ValidatorFn[] {
        const validators = [];

        if (config.required) {
            validators.push(Validators.required);
        }

        let bodyElement;
        switch (config.type) {
            case ElementType.Checkbox:
            case ElementType.Radio:
            case ElementType.Select: {
                break;
            }

            case ElementType.Textarea:
                bodyElement = config as TextareaElementModel;

                if (bodyElement.minlength) {
                    validators.push(
                        Validators.minLength(bodyElement.minlength)
                    );
                }

                if (bodyElement.maxlength) {
                    validators.push(
                        Validators.maxLength(bodyElement.maxlength)
                    );
                }

                break;
            case ElementType.Input:
            default:
                bodyElement = config as InputElementModel;

                if (bodyElement.typeInHTML === InputHTMLTypes.EMAIL) {
                    validators.push(Validators.email);
                }

                if (
                    [
                        InputHTMLTypes.TEXT,
                        InputHTMLTypes.EMAIL,
                        InputHTMLTypes.TEL,
                    ].includes(bodyElement.typeInHTML)
                ) {
                    if (bodyElement.min) {
                        validators.push(
                            Validators.minLength(bodyElement.min as number)
                        );
                    }

                    if (bodyElement.max) {
                        validators.push(
                            Validators.maxLength(bodyElement.max as number)
                        );
                    }
                }

                if (bodyElement.typeInHTML === InputHTMLTypes.NUMBER) {
                    if (bodyElement.min) {
                        validators.push(
                            Validators.min(bodyElement.min as number)
                        );
                    }

                    if (bodyElement.max) {
                        validators.push(
                            Validators.max(bodyElement.max as number)
                        );
                    }
                }

                if (bodyElement.typeInHTML === InputHTMLTypes.DATE) {
                    if (bodyElement.min) {
                        validators.push(
                            minDateValidator(bodyElement.min as Date)
                        );
                    }

                    if (bodyElement.max) {
                        validators.push(
                            maxDateValidator(bodyElement.max as Date)
                        );
                    }
                }

                break;
        }
        return validators;
    }

    private setBottomElementsFormGroup() {
        return this.skipConsentCheckbox
            ? this.formBuilder.group({})
            : this.formBuilder.group({
                  checked: [false, Validators.requiredTrue],
              });
    }

    private prefillValues(): void {
        const bodyGroup = this.registrationFormGroup()?.get('body');
        if (!bodyGroup) {
            return;
        }

        const categoryControls = (bodyGroup as FormGroup).controls;

        for (const categoryControl in categoryControls) {
            const categoryGroup = bodyGroup?.get([
                categoryControl,
            ]) as FormGroup;

            if (!categoryGroup) {
                continue;
            }

            const controls = categoryGroup?.controls;
            for (const field in controls) {
                const control = controls[field];
                switch (field) {
                    case 'email':
                        if (this.clientProfile.email) {
                            control.setValue(this.clientProfile.email);
                        }
                        break;
                    case 'phone':
                        if (this.clientProfile.phone) {
                            control.setValue(this.clientProfile.phone);
                        }
                        break;
                    case 'contactInfo1':
                        if (this.clientProfile.person) {
                            control.setValue(
                                this.clientProfile.person.givenName
                            );
                        } else if (this.clientProfile.organization) {
                            control.setValue(
                                this.clientProfile.organization.companyName
                            );
                        }
                        break;
                    case 'contactInfo2':
                        if (this.clientProfile.person) {
                            control.setValue(
                                this.clientProfile.person.familyName
                            );
                        } else if (this.clientProfile.organization) {
                            control.setValue(
                                this.clientProfile.organization.representative
                            );
                        }
                        break;
                    case 'userLanguage':
                        control.setValue(this.clientProfile.userLanguage);
                        break;
                    case 'dateOfBirth':
                        if (this.clientProfile.person?.dateOfBirth) {
                            control.setValue(
                                this.clientProfile.person.dateOfBirth
                            );
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'realWorldId':
                        if (this.clientProfile.organization?.realWorldId) {
                            control.setValue(
                                this.clientProfile.organization.realWorldId
                            );
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'street':
                        if (this.clientProfile.address) {
                            control.setValue(this.clientProfile.address.street);
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'houseNumber':
                        if (this.clientProfile.address) {
                            control.setValue(
                                this.clientProfile.address.houseNumber
                            );
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'zipCode':
                        if (this.clientProfile.address) {
                            control.setValue(
                                this.clientProfile.address.zipCode
                            );
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'city':
                        if (this.clientProfile.address) {
                            control.setValue(this.clientProfile.address.city);
                        } else {
                            control.setValue('');
                        }
                        break;
                    case 'countryCode':
                        if (this.clientProfile.address) {
                            control.setValue(
                                this.clientProfile.address.countryCode
                            );
                        } else {
                            control.setValue('');
                        }
                        break;
                    default:
                        if (this.bodyData[field]) {
                            control.setValue(this.bodyData[field]);
                        } else {
                            control.setValue('');
                        }
                }
            }
        }
        this.checkboxDirty.set(false);
    }

    private processFormData(): UpsertClientProfileDetailRequest {
        const bodyFormGroup = this.registrationFormGroup()?.get(
            'body'
        ) as FormGroup;

        const defaultFormGroup = bodyFormGroup?.get(
            ConstantsService.REGISTRATION_FORM_DEFAULT_CATEGORY_NAME
        ) as FormGroup;

        if (defaultFormGroup === null) {
            // corner case - sends incomplete data to BE and results in a failed registration
            return {} as UpsertClientProfileDetailRequest;
        }

        const body = {
            email: defaultFormGroup.get('email')?.value,
            phone: defaultFormGroup.get('phone')?.value,
            userLanguage: defaultFormGroup.get('userLanguage')?.value,
        } as UpsertClientProfileDetailRequest;

        switch (this.clientProfileType()) {
            case ClientProfileType.Organization: {
                body.organization = {
                    companyName: defaultFormGroup?.get('contactInfo1')?.value,
                    representative:
                        defaultFormGroup?.get('contactInfo2')?.value,
                } as Organization;
                if (defaultFormGroup?.get('realWorldId')?.value) {
                    body.organization.realWorldId =
                        defaultFormGroup?.get('realWorldId')?.value;
                }
                break;
            }
            case ClientProfileType.Person:
            default: {
                body.person = {
                    givenName: defaultFormGroup.get('contactInfo1')?.value,
                    familyName: defaultFormGroup.get('contactInfo2')?.value,
                } as Person;
                const dateOfBirthValue =
                    defaultFormGroup?.get('dateOfBirth')?.value;
                if (dateOfBirthValue) {
                    body.person.dateOfBirth =
                        this.formatDateForStorage(dateOfBirthValue) ?? '';
                }
                break;
            }
        }

        body.customFields = {};

        const dateInputControlNames = this.getDateInputControlNames();

        // Address (empty values -> empty string is present in the form group)
        body.address = {
            street: defaultFormGroup.get('street')?.value ?? '',
            houseNumber: defaultFormGroup.get('houseNumber')?.value ?? '',
            zipCode: defaultFormGroup.get('zipCode')?.value ?? '',
            city: defaultFormGroup.get('city')?.value ?? '',
            countryCode: defaultFormGroup.get('countryCode')?.value ?? '',
        } as Address;

        // Custom fields from default category
        for (const field in defaultFormGroup.controls) {
            // Skip elements which are already present in the body object
            if (this.PREDEFINED_DEFAULT_FIELDS.includes(field)) {
                continue;
            }

            body.customFields[field] = this.serializeCustomFieldValue(
                dateInputControlNames,
                field,
                defaultFormGroup.get([field])?.value
            );
        }

        // Custom fields from remaining categories
        const otherCategories = Object.entries(bodyFormGroup.controls).filter(
            (entry) =>
                entry[0] !==
                ConstantsService.REGISTRATION_FORM_DEFAULT_CATEGORY_NAME
        );

        // Flatten the custom fields from remaining categories
        for (const category of otherCategories) {
            const categoryGroup = category[1] as FormGroup;
            for (const field in categoryGroup.controls) {
                if (this.PREDEFINED_DEFAULT_FIELDS.includes(field)) {
                    continue;
                }

                body.customFields[field] = this.serializeCustomFieldValue(
                    dateInputControlNames,
                    field,
                    categoryGroup.get([field])?.value
                );
            }
        }
        return body;
    }

    private getDateInputControlNames(): Set<string> {
        const names = new Set<string>();
        const form = this.registrationForm();
        if (!form?.body?.length) {
            return names;
        }

        for (const category of form.body) {
            for (const element of category.elements) {
                if (element.type !== ElementType.Input) {
                    continue;
                }

                const input = element as InputElementModel;
                if (input.typeInHTML !== InputHTMLTypes.DATE) {
                    continue;
                }

                const controlName = input.name.translations[this.defaultLang];
                if (controlName) {
                    names.add(controlName);
                }
            }
        }

        return names;
    }

    private serializeCustomFieldValue(
        dateInputControlNames: Set<string>,
        controlName: string,
        raw: unknown
    ): unknown {
        if (!dateInputControlNames.has(controlName)) {
            return raw;
        }

        if (raw === null || raw === undefined || raw === '') {
            return '';
        }

        return this.formatDateForStorage(raw as Date | string) ?? '';
    }

    private formatDateForStorage(value: Date | string): string | null {
        return this.datePipe.transform(value, this.PROFILE_DATE_STORAGE_FORMAT);
    }
}
